import React, { useState, useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { DatePicker, Select } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import dayjs from "dayjs";

// Components
import Navbar from "../../Components/Navbar/Navbar";
import InputError from "../../Components/InputError/InputError";

// API calls and validation
import {
    createTransaction,
    updateTransaction,
    getTransaction,
} from "../../Helpers/apiCalls/transactionApi";
import { getAllBanks } from "../../Helpers/apiCalls/banksAPi";
import { validateTransactionCreate, validateTransactionUpdate } from "../../Helpers/Validation/Transaction/TransactionValidation";

// Utils
import {
    toastStyle,
    getTodayDateISO,
    numberFormat,
} from "../../Helpers/Utils/Common";


// CSS
import "./Transaction.css";
import "../PettyCash/PettyCash.css";

function TransactionForm({ add, edit }) {
    let navigate = useNavigate();
    const { id } = useParams();
    const [inactive, setInactive] = useState(true);

    // State for form data
    const [transactionData, setTransactionData] = useState({
        transaction_date: getTodayDateISO(),
        reference_no: "",
        bank_from_id: "",
        bank_to_id: "",
        amount: "",
        remarks: "",
    });

    // State for dropdowns
    const [banksData, setBanksData] = useState([]);

    const [bankToOptions, setBankToOptions] = useState([]);

    // State for validation
    const [isError, setIsError] = useState({
        transaction_date: false,
        reference_no: false,
        bank_from_id: false,
        bank_to_id: false,
        amount: false,
        remarks: false,
    });

    // State for loading
    const [isClicked, setIsClicked] = useState(false);

    // Fetch banks
    async function fetchBanks() {
        const response = await getAllBanks();
        if (response?.data) {
            let result = response.data.data.map((bank) => ({
                label: `${bank.bank_name} - ${bank.account_name}`,
                value: bank.id,
                bank_name: bank.bank_name,
                account_name: bank.account_name,
                current_balance: bank.current_bal,
            }));
            setBanksData(result);
        }
    }



    // Fetch transaction data for edit mode
    async function fetchTransactionData() {
        if (edit && id) {
            const response = await getTransaction(id);
            if (response?.data) {
                const data = response.data.data;
                setTransactionData({
                    transaction_date: data.transaction_date,
                    reference_no: data.reference_no,
                    bank_from_id: data.bank_from_id.toString(),
                    bank_to_id: data.bank_to_id.toString(),
                    amount: data.amount.toString(),
                    remarks: data.remarks || "",
                });
            }
        }
    }

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setTransactionData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear validation error when user starts typing
        if (isError[name]) {
            setIsError((prev) => ({
                ...prev,
                [name]: false,
            }));
        }
    };

    // Handle date change
    const handleDateChange = (date) => {
        setTransactionData((prev) => ({
            ...prev,
            transaction_date: date ? date.format("YYYY-MM-DD") : "",
        }));

        if (isError.transaction_date) {
            setIsError((prev) => ({
                ...prev,
                transaction_date: false,
            }));
        }
    };

    // Handle bank from selection
    const handleBankFromChange = (value) => {
        const bankFromId = value || "";
        setTransactionData((prev) => ({
            ...prev,
            bank_from_id: bankFromId,
            bank_to_id: prev.bank_to_id === bankFromId ? "" : prev.bank_to_id, // Clear bank_to if same as bank_from
        }));

        // Update bank_to options (exclude selected bank_from)
        const filteredBanks = banksData.filter(bank => bank.value !== bankFromId);
        setBankToOptions(filteredBanks);

        if (isError.bank_from_id) {
            setIsError((prev) => ({
                ...prev,
                bank_from_id: false,
            }));
        }
    };

    // Handle bank to selection
    const handleBankToChange = (value) => {
        setTransactionData((prev) => ({
            ...prev,
            bank_to_id: value || "",
        }));

        if (isError.bank_to_id) {
            setIsError((prev) => ({
                ...prev,
                bank_to_id: false,
            }));
        }
    };

// Create transaction
async function handleCreate() {
    if (validateTransactionCreate(transactionData, setIsError)) {
        setIsClicked(true);
        const response = await createTransaction(transactionData);

        if (response?.data) {
            if (response.data.status === "success") {
                toast.success(`Transaction created successfully!`, {
                    style: toastStyle(),
                });
                setTimeout(() => navigate("/transaction"), 1000);
            } else if (response.data.status === "error") {
                toast.error(response.data.response || "Error creating transaction", {
                    style: toastStyle(),
                });
            }
        } else if (response?.error) {
            const errorMsg = response.error?.data?.messages?.error || response.error?.response || "Error creating transaction";
            toast.error(errorMsg, {
                style: toastStyle(),
            });
        } else {
            toast.error("Error creating transaction", {
                style: toastStyle(),
            });
        }
        setIsClicked(false);
    } else {
        toast.error("Please fill in all required fields correctly", {
            style: toastStyle(),
        });
    }
}

    // Update transaction
    async function handleUpdate() {
        if (validateTransactionUpdate(transactionData, setIsError)) {
            setIsClicked(true);
            const response = await updateTransaction(id, transactionData);

            if (response?.data) {
                toast.success("Transaction updated successfully!", {
                    style: toastStyle(),
                });
                setTimeout(() => navigate("/transaction"), 1000);
            } else {
                toast.error("Error updating transaction", {
                    style: toastStyle(),
                });
            }
            setIsClicked(false);
        } else {
            toast.error("Please fill in all required fields correctly", {
                style: toastStyle(),
            });
        }
    }

    // Get selected bank info for display
    const getSelectedBankInfo = (bankId) => {
        const bank = banksData.find(b => b.value === parseInt(bankId));
        return bank ? `Current Balance: ${numberFormat(bank.current_balance)}` : "";
    };

    // Initialize data
    useEffect(() => {
        fetchBanks();
        if (edit) {
            fetchTransactionData();
        }
    }, []);

    // Update bank_to options when banksData or bank_from_id changes
    useEffect(() => {
        const filteredBanks = banksData.filter(bank => bank.value !== parseInt(transactionData.bank_from_id));
        setBankToOptions(filteredBanks);
    }, [banksData, transactionData.bank_from_id]);

    return (
        <div>
            <div className="page">
                <Navbar
                    onCollapse={(inactive) => {
                        setInactive(inactive);
                    }}
                    active={"TRANSFER"}
                />
            </div>
            <div className={`manager-container ${inactive ? "inactive" : "active"}`}>
                {/* Header */}
                <Row className="mb-4">
                    <Col xs={12}>
                        <h1 className="page-title">
                            {add ? "ADD NEW TRANSACTION" : "EDIT TRANSACTION"}
                        </h1>
                    </Col>
                </Row>

                    {/* Form */}
                    <div className="edit-form">
                        <Row>
                            <Col md={4}>
                                <div className="nc-modal-custom-row">
                                    <span className="edit-label">
                                        TRANSACTION DATE <span className="required">*</span>
                                    </span>
                                    <DatePicker
                                        format="YYYY-MM-DD"
                                        value={transactionData.transaction_date ? dayjs(transactionData.transaction_date) : null}
                                        onChange={handleDateChange}
                                        style={{ width: "100%" }}
                                        className="nc-modal-custom-input"
                                    />
                                    <InputError
                                        isValid={isError.transaction_date}
                                        message="Transaction date is required"
                                    />
                                </div>
                            </Col>

                            <Col md={4}>
                                <div className="nc-modal-custom-row">
                                    <span className="edit-label">
                                        FROM BANK <span className="required">*</span>
                                    </span>
                                    <Select
                                        showSearch
                                        placeholder="Select..."
                                        className="nc-modal-custom-text custom-select"
                                        style={{ width: "100%" }}
                                        options={banksData}
                                        value={transactionData.bank_from_id || undefined}
                                        onChange={handleBankFromChange}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                    {transactionData.bank_from_id && (
                                        <small className="text-muted">
                                            {getSelectedBankInfo(transactionData.bank_from_id)}
                                        </small>
                                    )}
                                    <InputError
                                        isValid={isError.bank_from_id}
                                        message="From bank is required"
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="nc-modal-custom-row">
                                    <span className="edit-label">
                                        TO BANK <span className="required">*</span>
                                    </span>
                                    <Select
                                        showSearch
                                        placeholder="Select..."
                                        className="nc-modal-custom-text custom-select"
                                        style={{ width: "100%" }}
                                        options={bankToOptions}
                                        value={transactionData.bank_to_id || undefined}
                                        onChange={handleBankToChange}
                                        disabled={!transactionData.bank_from_id}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                    {transactionData.bank_to_id && (
                                        <small className="text-muted">
                                            {getSelectedBankInfo(transactionData.bank_to_id)}
                                        </small>
                                    )}
                                    <InputError
                                        isValid={isError.bank_to_id}
                                        message={typeof isError.bank_to_id === "string" ? isError.bank_to_id : "To bank is required"}
                                    />
                                </div>
                            </Col>
                        </Row>
                           <Row>
                            <Col md={4}>
                                <div className="nc-modal-custom-row">
                                    <span className="edit-label">
                                        REFERENCE NUMBER <span className="required">*</span>
                                    </span>
                                    <Form.Control
                                        type="text"
                                        name="reference_no"
                                        value={transactionData.reference_no}
                                        onChange={handleChange}
                                        className="nc-modal-custom-input"
                                    />
                                    <InputError
                                        isValid={isError.reference_no}
                                        message={typeof isError.reference_no === "string" ? isError.reference_no : "Reference number is required"}
                                    />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="nc-modal-custom-row">
                                    <span className="edit-label">
                                        AMOUNT <span className="required">*</span>
                                    </span>
                                    <Form.Control
                                        type="number"
                                        name="amount"
                                        value={transactionData.amount}
                                        onChange={handleChange}
                                        step="0.01"
                                        min="0"
                                        className="nc-modal-custom-input"
                                    />
                                    <InputError
                                        isValid={isError.amount}
                                        message="Amount is required and must be greater than 0"
                                    />
                                </div>
                            </Col>
                    
                            <Col md={4}>
                                <div className="nc-modal-custom-row">
                                    <span className="edit-label">REMARKS</span>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="remarks"
                                        value={transactionData.remarks}
                                        onChange={handleChange}
                                        className="nc-modal-custom-input"
                                    />
                                    <InputError
                                        isValid={isError.remarks}
                                        message="Remarks cannot be empty if provided"
                                    />
                                </div>
                            </Col>
                        </Row>

                        {/* Action Buttons */}
                        <div className="d-flex justify-content-end pt-5 pb-3">
                            <button
                                type="button"
                                className="button-secondary me-3"
                                onClick={() => navigate("/transaction")}
                                disabled={isClicked}
                            >
                                Cancel
                            </button>
                            {isClicked ? (
                                <div className="button-primary d-flex justify-content-center">
                                    <span>Processing...</span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="button-primary"
                                    onClick={add ? handleCreate : handleUpdate}
                                >
                                    {add ? "Submit" : "Update Transaction"}
                                </button>
                            )}
                        </div>
                    </div>
            </div>
        </div>
    );
}

export default TransactionForm;
